import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Layer, Map, Marker, latLng, latLngBounds } from 'leaflet';
import { HttpService, MapService } from '../../../core/services';
import { MapModel, MapSettings } from '../models';
import { ConnectionsLayerService, ContacPlacesLayerService, RouteLayerService, StopsLayerService, UserLayerService } from '.';

@Injectable({
  providedIn: 'root'
})
export class LayerService {
  private map: Map | undefined;
  private mapSettings!: MapSettings;
  private readonly connectionsLayerService: ConnectionsLayerService = new ConnectionsLayerService();
  readonly stopsLayerService: StopsLayerService = new StopsLayerService();
  readonly contactPlacesLayerService: ContacPlacesLayerService = new ContacPlacesLayerService();
  readonly routeLayerService: RouteLayerService = new RouteLayerService();

  constructor(
    private readonly router: Router,
    private readonly httpService: HttpService,
    private readonly mapService: MapService,
    readonly userLayerService: UserLayerService)
  {
    this.layers.push(userLayerService.userLayer);

    mapService.mapCenterOn$.subscribe(connId => {
      this.deselectMarkers();
      this.connectionsLayerService.selectMarker(connId);

      const coords = this.routeLayerService.pathCoords;
      const conn = this.connectionsLayerService.getConn(connId);
      if (conn?.lat && conn.lon)
        coords.push(latLng(conn.lat, conn.lon));

      const bounds = latLngBounds(coords.map((c) => {
        return [c.lat, c.lng];
      }));
      this.map?.fitBounds(bounds, { animate: false, paddingTopLeft: [420, 10], paddingBottomRight: [10, 10] });
    });
  }

  layers: Layer[] = [
    this.connectionsLayerService.connLayer,
    this.stopsLayerService.stopsLayer,
    this.contactPlacesLayerService.contactPlacesLayer
  ];

  init(map: Map, mapSettings: MapSettings) {
    this.map = map;
    this.mapSettings = mapSettings;
    this.stopsLayerService.init(this.router);
    this.contactPlacesLayerService.init(this.router, map, this.httpService, this.stopsLayerService);
    this.connectionsLayerService.init(this.router, this.mapService, mapSettings);
    this.routeLayerService.init(map, this.mapService);
    this.layers.push(this.routeLayerService.arrowLayer);
    this.layers.push(this.routeLayerService.pathLayer);
  }

  moveTo(lat: number, lon: number, desiredZoom: number = 10) {
    if (this.map) {
      const zoom = Math.max(this.map.getZoom(), desiredZoom);
      this.map.setView(latLng(lat, lon), zoom);
    }
  }

  moveToContactPlace(id: number, lat: number, lon: number) {
    if (this.map) {
      this.deselectMarkers();
      this.contactPlacesLayerService.selectMarker(id);
      const zoom = Math.max(this.map.getZoom(), 15);
      this.map.setView(latLng(lat, lon), zoom);
    }
  }

  redraw(model: MapModel) {
    if (model.stops) {
      this.stopsLayerService.redraw(model.stops);
    }
    if (model.connections) {
      const activeMarkerPromise = this.connectionsLayerService.redraw(model.connections);
      activeMarkerPromise.then((activeMarker: Marker | undefined) => {
        if (activeMarker) {
          this.routeLayerService.redrawPath(activeMarker);
        }
      });
    }
  }

  changeDetail() {
    if (this.mapSettings?.detailed) {
      if (this.mapSettings?.extraDetailed) {
        this.mapSettings.extraDetailed = false;
        this.mapSettings.detailed = false;
      }
      else
        this.mapSettings.extraDetailed = true;
    }
    else if (this.mapSettings && !this.mapSettings?.extraDetailed) {
      this.mapSettings.detailed = true;
    }

    this.connectionsLayerService.redraw();
  }

  deselectMarkers() {
    this.connectionsLayerService.selectMarker(undefined);
    this.contactPlacesLayerService.selectMarker(undefined);
  }

  getConnectionInMap(connId: string) {
    return this.connectionsLayerService.getConn(connId);
  }
}
